<script lang="ts">
	import { get } from 'svelte/store';
	import { spaceAdmin } from '../../stores';
	import {
		ShareNetwork as ShareIcon,
		Copy as CopyIcon,
		Check as CheckIcon,
		Globe as GlobeIcon,
		Users as UsersIcon,
	} from 'phosphor-svelte';
	import * as toast from '../../lib/toast';

	type LinkAccess = 'none' | 'viewer' | 'editor';
	interface SpaceMember { id: string; email: string; role: string; }

	let showPopover = false;
	let popoverElement: HTMLElement | null = null;
	let linkAccess: LinkAccess = 'none';
	let members: SpaceMember[] = [];
	let isLoadingMembers = false;
	let copied = false;
	let isUpdatingAccess = false;

	$: currentSpace = $spaceAdmin;
	$: isOwnerOrAdmin = currentSpace?.role === 'owner' || currentSpace?.role === 'admin';

	const accessOptions: { value: LinkAccess; label: string }[] = [
		{ value: 'none', label: 'Off' },
		{ value: 'viewer', label: 'View' },
		{ value: 'editor', label: 'Edit' },
	];

	const accessDescriptions: Record<LinkAccess, string> = {
		none: 'Only invited members can access this space',
		viewer: 'Anyone with the link can view',
		editor: 'Anyone with the link can edit',
	};

	function togglePopover() {
		showPopover = !showPopover;
		if (showPopover) {
			loadShareData();
		}
	}

	async function loadShareData() {
		const sp = get(spaceAdmin);
		if (!sp) return;

		linkAccess = (sp.linkAccess as LinkAccess) || 'none';

		isLoadingMembers = true;
		try {
			members = await sp.listUsers();
		} catch (error) {
			console.error('Failed to load members:', error);
			members = [];
		} finally {
			isLoadingMembers = false;
		}
	}

	async function setLinkAccess(access: LinkAccess) {
		const sp = get(spaceAdmin);
		if (!sp || !isOwnerOrAdmin || access === linkAccess || isUpdatingAccess) return;

		isUpdatingAccess = true;
		try {
			await sp.setLinkAccess(access);
			linkAccess = access;
		} catch (error) {
			console.error('Failed to update link access:', error);
			toast.error('Failed to update sharing settings');
		} finally {
			isUpdatingAccess = false;
		}
	}

	async function copyLink() {
		try {
			await navigator.clipboard.writeText(window.location.href);
			copied = true;
			toast.success('Link copied to clipboard');
			setTimeout(() => { copied = false; }, 2000);
		} catch (error) {
			console.error('Failed to copy link:', error);
			toast.error('Failed to copy link');
		}
	}

	function handleClickOutside(event: MouseEvent) {
		if (!showPopover || !popoverElement) return;
		if (popoverElement.contains(event.target as Node)) return;
		showPopover = false;
	}

	function getRoleBadgeClass(role: string): string {
		switch (role) {
			case 'owner': return 'role-owner';
			case 'admin': return 'role-admin';
			case 'editor': return 'role-editor';
			case 'viewer': return 'role-viewer';
			default: return '';
		}
	}
</script>

<svelte:window on:click={handleClickOutside} />

<div class="share-container" bind:this={popoverElement}>
	<button
		class="share-button"
		class:open={showPopover}
		type="button"
		aria-label="Share this space"
		on:click={togglePopover}
	>
		<ShareIcon size={16} weight="bold" />
		<span class="share-label">Share</span>
	</button>

	{#if showPopover}
		<div class="share-popover">
			<!-- Link sharing section -->
			<div class="popover-section">
				<div class="section-header">
					<span class="section-icon">
						<GlobeIcon size={16} />
					</span>
					<span class="section-title">Link sharing</span>
				</div>

				{#if isOwnerOrAdmin}
					<div class="access-toggle" class:disabled={isUpdatingAccess}>
						{#each accessOptions as option}
							<button
								class="toggle-option"
								class:active={linkAccess === option.value}
								type="button"
								disabled={isUpdatingAccess}
								on:click={() => setLinkAccess(option.value)}
							>
								{option.label}
							</button>
						{/each}
					</div>
				{:else}
					<div class="access-readonly">
						{#each accessOptions as option}
							<span
								class="toggle-option-static"
								class:active={linkAccess === option.value}
							>
								{option.label}
							</span>
						{/each}
					</div>
				{/if}

				<p class="access-description">{accessDescriptions[linkAccess]}</p>
			</div>

			<!-- Copy link -->
			<div class="copy-link-section">
				<button class="copy-link-button" type="button" on:click={copyLink}>
					<span class="copy-link-icon">
						{#if copied}
							<CheckIcon size={16} />
						{:else}
							<CopyIcon size={16} />
						{/if}
					</span>
					<span class="copy-link-text">{copied ? 'Copied!' : 'Copy link'}</span>
				</button>
			</div>

			<div class="popover-divider"></div>

			<!-- Members section -->
			<div class="popover-section">
				<div class="section-header">
					<span class="section-icon">
						<UsersIcon size={16} />
					</span>
					<span class="section-title">Members</span>
					{#if !isLoadingMembers}
						<span class="member-count">{members.length}</span>
					{/if}
				</div>

				<div class="members-list">
					{#if isLoadingMembers}
						<div class="members-loading">Loading...</div>
					{:else if members.length === 0}
						<div class="members-empty">No members</div>
					{:else}
						{#each members as member}
							<div class="member-row">
								<div class="member-avatar">
									<span class="member-initial">{(member.email[0] || '?').toUpperCase()}</span>
								</div>
								<span class="member-email">{member.email}</span>
								<span class="role-badge {getRoleBadgeClass(member.role)}">{member.role}</span>
							</div>
						{/each}
					{/if}
				</div>
			</div>
		</div>
	{/if}
</div>

<style>
	.share-container {
		position: relative;
	}

	/* Share button */
	.share-button {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 0 10px 0 8px;
		height: 34px;

		background: transparent;
		color: var(--text);
		border: 1px solid transparent;
		border-radius: 10px;
		cursor: pointer;
		font-size: 13px;
		font-weight: 600;

		transition:
			background 0.15s ease,
			border-color 0.15s ease,
			color 0.15s ease,
			box-shadow 0.15s ease,
			transform 0.06s ease;
	}

	.share-button:hover {
		background: var(--hover);
		border-color: var(--border);
	}

	.share-button:active {
		transform: scale(0.96);
	}

	.share-button.open {
		background: var(--hover);
		border-color: var(--accent);
		color: var(--accent);
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 16%, transparent);
	}

	.share-button :global(svg) {
		flex-shrink: 0;
	}

	.share-button.open :global(svg) {
		color: var(--accent);
	}

	.share-label {
		user-select: none;
	}

	/* Popover */
	.share-popover {
		position: absolute;
		top: calc(100% + 10px);
		right: 0;
		width: 320px;

		background: var(--panelSolid);
		border: 1px solid var(--border);
		border-radius: 12px;
		box-shadow:
			0px 5.23px 6.54px -3.92px rgba(0, 0, 0, 0.1),
			0px 13.08px 16.35px -3.27px rgba(0, 0, 0, 0.1);

		padding: 6px;
		z-index: 10000;
	}

	/* Sections */
	.popover-section {
		padding: 8px 10px;
	}

	.section-header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
	}

	.section-icon {
		display: flex;
		align-items: center;
		color: var(--textMuted);
	}

	.section-title {
		font-size: 11px;
		font-weight: 600;
		color: var(--textMuted);
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.member-count {
		font-size: 11px;
		font-weight: 500;
		color: var(--faint);
		margin-left: auto;
	}

	.popover-divider {
		height: 1px;
		background: var(--border);
		margin: 2px 6px;
	}

	/* Segmented toggle control */
	.access-toggle,
	.access-readonly {
		display: flex;
		gap: 2px;
		padding: 3px;
		background: var(--hover);
		border-radius: 8px;
	}

	.access-toggle.disabled {
		opacity: 0.6;
		pointer-events: none;
	}

	.toggle-option,
	.toggle-option-static {
		flex: 1;
		padding: 6px 0;

		font-size: 12px;
		font-weight: 500;
		color: var(--textMuted);
		text-align: center;

		background: transparent;
		border: none;
		border-radius: 6px;
		cursor: pointer;
		user-select: none;

		transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
	}

	.toggle-option:hover:not(.active):not(:disabled) {
		color: var(--text);
	}

	.toggle-option.active,
	.toggle-option-static.active {
		background: var(--panelSolid);
		color: var(--text);
		font-weight: 600;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
	}

	.toggle-option-static {
		cursor: default;
	}

	/* Access description */
	.access-description {
		font-size: 12px;
		color: var(--textMuted);
		margin: 8px 0 0 0;
		padding: 0;
	}

	/* Copy link */
	.copy-link-section {
		padding: 0 6px 2px;
	}

	.copy-link-button {
		display: flex;
		align-items: center;
		gap: 8px;
		width: 100%;
		padding: 8px 10px;

		background: transparent;
		border: none;
		border-radius: 8px;
		color: var(--accent);
		cursor: pointer;
		font-size: 13px;
		font-weight: 500;

		transition: background 0.1s ease;
	}

	.copy-link-button:hover {
		background: var(--hover);
	}

	.copy-link-button:active {
		transform: scale(0.98);
	}

	.copy-link-icon {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	/* Members list */
	.members-list {
		display: flex;
		flex-direction: column;
		gap: 2px;
		max-height: 200px;
		overflow-y: auto;
	}

	.members-loading,
	.members-empty {
		font-size: 13px;
		color: var(--textMuted);
		padding: 8px 4px;
	}

	.member-row {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 4px;
		border-radius: 6px;
	}

	.member-avatar {
		width: 28px;
		height: 28px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		background: var(--hover);
		border-radius: 50%;
	}

	.member-initial {
		font-size: 12px;
		font-weight: 600;
		color: var(--textMuted);
		user-select: none;
	}

	.member-email {
		flex: 1;
		font-size: 13px;
		color: var(--text);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
	}

	.role-badge {
		font-size: 11px;
		font-weight: 600;
		padding: 2px 8px;
		border-radius: 4px;
		text-transform: capitalize;
		flex-shrink: 0;
		user-select: none;
	}

	.role-owner {
		background: rgba(124, 58, 237, 0.1);
		color: var(--accent);
	}

	.role-admin {
		background: rgba(124, 58, 237, 0.08);
		color: var(--accent);
	}

	.role-editor {
		background: rgba(46, 144, 250, 0.1);
		color: var(--userAccent);
	}

	.role-viewer {
		background: var(--hover);
		color: var(--textMuted);
	}
</style>
